/**
 * API Services
 * 
 * Axios-based API client for backend communication
 */

import axios from 'axios'
import * as Sentry from '@sentry/react'
import { tabClinicHeader } from '../lib/tabClinic'

// S3 public bucket URL for product images
const S3_PUBLIC_URL = 'https://5axis-intake-public.s3.amazonaws.com'

/**
 * Build a public S3 URL for a product image from the dosage.img DB path.
 * DB paths vary (e.g. /assets/img/products/phentermine.png) but all product
 * images live flat under products/ in the bucket.
 * Returns null if no dosageImg provided.
 */
export function getProductImageUrl(dosageImg) {
  if (!dosageImg) return null
  const filename = dosageImg.split('/').pop()
  if (!filename) return null
  return `${S3_PUBLIC_URL}/products/${filename}`
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api', // Vite proxy will forward to http://localhost:3001/api
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage - check both locations
    let token = null
    
    // First check the direct auth-token storage
    token = localStorage.getItem('auth-token')
    
    // Fallback to Zustand persist storage
    if (!token) {
      const authStorage = localStorage.getItem('auth-storage')
      if (authStorage) {
        try {
          const { state } = JSON.parse(authStorage)
          token = state?.token
        } catch (error) {
          console.error('Error reading auth storage:', error)
        }
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // This tab's clinic, so another tab switching clinics cannot change what
    // this one is shown. See lib/tabClinic.
    const tabClinic = tabClinicHeader()
    if (tabClinic && !config.headers['X-Clinic-Id']) {
      config.headers['X-Clinic-Id'] = tabClinic
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors (token expired/invalid)
    if (error.response?.status === 401) {
      console.error('🔒 401 Unauthorized error:', error.config?.url, error.response?.data)
      // Only auto-logout for non-clinic-switch errors to help debug
      if (!error.config?.url?.includes('switch-clinic')) {
        // Clear both auth storage locations and redirect to login
        localStorage.removeItem('auth-storage')
        localStorage.removeItem('auth-token')
        window.location.href = '/login'
      }
    }

    // Pages catch failed requests themselves, so the global handlers never see
    // them. 4xx answers (bad input, expired login, missing record) are expected.
    const status = error.response?.status
    if (!axios.isCancel(error) && (!status || status >= 500)) {
      Sentry.captureException(error, {
        tags: { status_code: status ?? 'network' },
        extra: { url: error.config?.url, method: error.config?.method, response: error.response?.data },
      })
    }

    return Promise.reject(error)
  }
)

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: (token) => api.post('/auth/logout', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  refresh: (token) => api.post('/auth/refresh', { token }),
  me: (token) => api.get('/auth/me', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  register: (userData) => api.post('/auth/register', userData),
  switchClinic: (clinicId) => api.post('/auth/switch-clinic', { clinicId })
}

// User Management API
export const userAPI = {
  list: (params = {}) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, passwordData) => api.post(`/users/${id}/change-password`, passwordData),
  getRoles: () => api.get('/users/meta/roles'),
  getReportViews: (report) => api.get(`/users/me/report-views/${report}`),
  saveReportViews: (report, views) => api.put(`/users/me/report-views/${report}`, { views }),
  getReportLayout: (report) => api.get(`/users/me/report-layout/${report}`),
  saveReportLayout: (report, layout) => api.put(`/users/me/report-layout/${report}`, { layout }),
}

// Clinic API
export const clinicAPI = {
  list: () => api.get('/clinics'),
  get: (id) => api.get(`/clinics/${id}`),
}

// Express clinic setup API
export const expressSetupAPI = {
  list: () => api.get('/express-setup'),
  get: (clinicId) => api.get(`/express-setup/${clinicId}`),
  updateTask: (clinicId, key, data) => api.patch(`/express-setup/${clinicId}/tasks/${key}`, data),
  addTask: (clinicId, data) => api.post(`/express-setup/${clinicId}/tasks`, data),
  verifySite: (clinicId) => api.post(`/express-setup/${clinicId}/verify-site`, null, { timeout: 30000 }),
  deleteTask: (clinicId, key) => api.delete(`/express-setup/${clinicId}/tasks/${key}`),
  updateFlags: (clinicId, data) => api.put(`/express-setup/${clinicId}/flags`, data),
  uploadAttachment: (clinicId, key, file) =>
    api.post(`/express-setup/${clinicId}/tasks/${key}/attachments`, file, {
      params: { filename: file.name, contentType: file.type || 'application/octet-stream' },
      headers: { 'Content-Type': 'application/octet-stream' },
      timeout: 120000,
    }),
  downloadAttachment: (clinicId, key, s3Key) =>
    api.get(`/express-setup/${clinicId}/tasks/${key}/attachments/download`, {
      params: { key: s3Key },
      responseType: 'blob',
      timeout: 120000,
    }),
  deleteAttachment: (clinicId, key, s3Key) =>
    api.delete(`/express-setup/${clinicId}/tasks/${key}/attachments`, { params: { key: s3Key } }),
}

// Express Portals fleet view API (super admin only)
export const expressPortalsAPI = {
  getFleet: (bypassCache) => api.get('/admin/express-portals/fleet', {
    params: bypassCache ? { bypassCache: true } : {},
    timeout: 60000,
  }),
  getFleetSummaries: (clinics, bypassCache) => api.post('/admin/express-portals/fleet/summaries', {
    clinics, bypassCache,
  }, { timeout: 120000 }),
  getClinicDetail: (clinicId, bypassCache) => api.get(`/admin/express-portals/clinics/${clinicId}`, {
    params: bypassCache ? { bypassCache: true } : {},
    timeout: 60000,
  }),
  getClinicDeployments: (clinicId, limit, bypassCache) => api.get(`/admin/express-portals/clinics/${clinicId}/deployments`, {
    params: { ...(limit ? { limit } : {}), ...(bypassCache ? { bypassCache: true } : {}) },
    timeout: 60000,
  }),
  getClinicMetadata: (clinicId) => api.get(`/admin/express-portals/clinics/${clinicId}/metadata`, {
    timeout: 60000,
  }),
  getClinicMetadataForEdit: (clinicId) => api.get(`/admin/express-portals/clinics/${clinicId}/metadata`, {
    params: { forEdit: true },
    timeout: 60000,
  }),
  updateClinicMetadata: (clinicId, metadata) => api.put(`/admin/express-portals/clinics/${clinicId}/metadata`, { metadata }, {
    timeout: 60000,
  }),
  presignLogo: (clinicId, type, ext) => api.post(`/admin/express-portals/clinics/${clinicId}/logo-presign`, { type, ext }, {
    timeout: 60000,
  }),
  getEnvVars: (clinicId) => api.get(`/admin/express-portals/clinics/${clinicId}/env-vars`, {
    timeout: 60000,
  }),
  saveEnvVars: (clinicId, envVars) => api.put(`/admin/express-portals/clinics/${clinicId}/env-vars`, { envVars }, {
    timeout: 60000,
  }),
  // Key NAMES only, in both directions — the server holds the values.
  getEnvDefaults: () => api.get('/admin/express-portals/env-defaults', { timeout: 60000 }),
  quickFixEnvVars: (clinicId, keys) => api.post(`/admin/express-portals/clinics/${clinicId}/env-vars/quick-fix`, { keys }, {
    timeout: 120000,
  }),
  // 120s: three sequential Postmark calls plus a Core metadata write.
  provisionPostmark: (clinicId, brandDisplayName, emailFrom) => api.post(`/admin/express-portals/clinics/${clinicId}/postmark`, { brandDisplayName, emailFrom }, {
    timeout: 120000,
  }),
  // Live Postmark reads: up to ~6 sequential calls (server, two templates, domains, domain detail, senders).
  getPostmarkStatus: (clinicId, bypassCache = false) => api.get(`/admin/express-portals/clinics/${clinicId}/postmark/status`, {
    params: bypassCache ? { bypassCache: true } : undefined,
    timeout: 60000,
  }),
  addPostmarkDomain: (clinicId) => api.post(`/admin/express-portals/clinics/${clinicId}/postmark/domain`, {}, {
    timeout: 60000,
  }),
  verifyPostmarkDomain: (clinicId) => api.post(`/admin/express-portals/clinics/${clinicId}/postmark/domain/verify`, {}, {
    timeout: 60000,
  }),
  sendPostmarkTestEmail: (clinicId) => api.post(`/admin/express-portals/clinics/${clinicId}/postmark/test-email`, {}, {
    timeout: 60000,
  }),
  // One Vercel env read plus one Apple lookup.
  getAppStoreRelease: (clinicId, bypassCache = false) => api.get(`/admin/express-portals/clinics/${clinicId}/app-store-release`, {
    params: bypassCache ? { bypassCache: true } : undefined,
    timeout: 60000,
  }),
  dispatchRollout: (clinicIds) => api.post('/admin/express-portals/rollouts', { clinics: clinicIds }, {
    timeout: 120000,
  }),
  getRolloutStatus: (rolloutId) => api.get(`/admin/express-portals/rollouts/${rolloutId}`, {
    timeout: 60000,
  }),
  // Provision Core Auth. The token goes out once and never comes back.
  getCoreAuthStatus: (clinicId) => api.get(`/admin/express-portals/clinics/${clinicId}/core-auth`, {
    timeout: 60000,
  }),
  saveCoreAuthEnv: (clinicId, mode, token) => api.put(`/admin/express-portals/clinics/${clinicId}/core-auth/env`, {
    mode, ...(token ? { token } : {}),
  }, { timeout: 60000 }),
  mirrorCoreAuthMetadata: (clinicId, mode) => api.put(`/admin/express-portals/clinics/${clinicId}/core-auth/metadata`, { mode }, {
    timeout: 60000,
  }),
  setFirebaseAccountCreation: (clinicId, value) => api.put(`/admin/express-portals/clinics/${clinicId}/core-auth/firebase-creation`, { value }, {
    timeout: 60000,
  }),
}

/**
 * Portal Auth — the Firebase -> core auth migration view.
 *
 * TEMPORARY: goes with Firebase. Separate from `expressPortalsAPI` because it
 * is a separate nav item answering a different question, and must load without
 * the Express Portals page having been opened first.
 *
 * `getRows` takes a BATCH, not the fleet: each row is a fan-out of Mongo
 * aggregations across one clinic's database, so the page slices the fleet the
 * way `ExpressPortalsPage.jsx` already slices summaries. The 120s timeout is
 * that batch's worst case, not one clinic's.
 */
export const portalAuthAPI = {
  getFleet: (bypassCache) => api.get('/admin/portal-auth/fleet', {
    params: bypassCache ? { bypassCache: true } : {},
    timeout: 60000,
  }),
  getRows: (clinics, bypassCache) => api.post('/admin/portal-auth/fleet/rows', {
    clinics, bypassCache,
  }, { timeout: 120000 }),
  getClinic: (clinicId, bypassCache) => api.get(`/admin/portal-auth/clinics/${clinicId}`, {
    params: bypassCache ? { bypassCache: true } : {},
    timeout: 60000,
  }),
  // The events behind one clickable stat. Params from `buildEventsParams`.
  getEvents: (clinicId, params) => api.get(`/admin/portal-auth/clinics/${clinicId}/events`, {
    params,
    timeout: 30000,
  }),
}

/**
 * Portal Impersonations — one clinic's impersonation log.
 *
 * Read-only by construction: there is no `post`, `put` or `delete` here and
 * there is none on the router either. That is the same rule `portalAuth` keeps,
 * and it matters more on this surface because these rows name patients.
 *
 * `portalImpersonations`, never `impersonation` on its own: this app already
 * has a super_admin-impersonates-a-user feature under that name, and the two
 * are unrelated systems that must not borrow each other's vocabulary.
 */
export const portalImpersonationsAPI = {
  /** The picker's options. Every clinic, whatever its Core status. */
  clinics: () => api.get('/admin/portal-impersonations/clinics', { timeout: 60000 }),
  /** `params.clinic` is REQUIRED — the log lives in that clinic's database. */
  list: (params = {}) => api.get('/admin/portal-impersonations', { params, timeout: 60000 }),
  get: (id, clinic) => api.get(`/admin/portal-impersonations/${id}`, {
    params: { clinic }, timeout: 30000,
  }),
}

// Physician API
export const physicianAPI = {
  list: (params = {}) => api.get('/physicians', { params }),
  get: (id) => api.get(`/physicians/${id}`),
  getStats: () => api.get('/physicians/stats/summary'),
  // Where the clinic's doctors come from: {source, shared, can_edit_shared}.
  source: () => api.get('/physicians/source'),
  create: (data) => api.post('/physicians', data),
  update: (id, data) => api.put(`/physicians/${id}`, data),
}

// Core's shared doctor directory and its release stream (super admins and the
// canManageSharedPhysicians grant; list is also readable from a clinic using it).
// No direct write: every change goes through publish, so it can be reverted.
export const sharedPhysiciansAPI = {
  list: () => api.get('/shared-physicians'),
  sources: () => api.get('/shared-physicians/sources'),
  listReleases: (params = {}) => api.get('/shared-physicians/releases', { params }),
  getRelease: (id) => api.get(`/shared-physicians/releases/${id}`),
  publish: (data) => api.post('/shared-physicians/releases/publish', data),
  revert: (id) => api.post(`/shared-physicians/releases/${id}/revert`),
}

// Pharmacy API
export const pharmacyAPI = {
  list: (params = {}) => api.get('/pharmacy', { params }),
  get: (id) => api.get(`/pharmacy/${id}`),
  getStats: () => api.get('/pharmacy/stats'),
  getPrograms: (id) => api.get(`/pharmacy/${id}/programs`),
  create: (pharmacyData) => api.post('/pharmacy', pharmacyData),
  update: (id, pharmacyData) => api.put(`/pharmacy/${id}`, pharmacyData),
  delete: (id) => api.delete(`/pharmacy/${id}`),
}

// Drug Program API
export const drugProgramAPI = {
  list: (params = {}) => api.get('/drug-programs', { params }),
  get: (id) => api.get(`/drug-programs/${id}`),
  getStats: () => api.get('/drug-programs/stats'),
  getPharmacies: (id) => api.get(`/drug-programs/${id}/pharmacies`),
  getTags: () => api.get('/drug-programs/tags'),
  create: (programData) => api.post('/drug-programs', programData),
  update: (id, programData) => api.put(`/drug-programs/${id}`, programData),
  delete: (id) => api.delete(`/drug-programs/${id}`),
  addPharmacy: (id, pharmacyData) => api.post(`/drug-programs/${id}/pharmacies`, pharmacyData),
  removePharmacy: (id, pharmacyId) => api.delete(`/drug-programs/${id}/pharmacies/${pharmacyId}`),
}

// Program Category API
export const programCategoryAPI = {
  list: (params = {}) => api.get('/program-categories', { params }),
  get: (id) => api.get(`/program-categories/${id}`),
  create: (data) => api.post('/program-categories', data),
  update: (id, data) => api.put(`/program-categories/${id}`, data),
}

// System API
export const systemAPI = {
  health: () => api.get('/health'),
  databaseHealth: () => api.get('/health/database'),
}

// Dashboard/Analytics API
export const dashboardAPI = {
  getPnL: (params = {}) => api.get('/dashboard/pnl', { params }),
  getAggregatePnL: (params = {}) => api.get('/dashboard/pnl/aggregate', { params, timeout: 60000 }),
  getSummary: () => api.get('/dashboard/summary'),
  // Today so far, against the same elapsed slice of yesterday.
  getOverview: () => api.get('/dashboard/overview', { timeout: 60000 }),
}

// Accounting API
export const accountingAPI = {
  getPharmacyCost: (params = {}) => api.get('/accounting/pharmacy-cost', { params, timeout: 60000 }),
  getAggregatePharmacyCost: (params = {}) => api.get('/accounting/pharmacy-cost/aggregate', { params, timeout: 90000 }),
  getMultiPnL: (params = {}) => api.get('/accounting/multi-pnl', { params, timeout: 90000 }),
  // Note: distinct from paymentsAPI.getRefunds, which is Manifold's own
  // all-time refund total. These are the dated, itemized accounting reports.
  getRefunds: (params = {}) => api.get('/accounting/refunds', { params, timeout: 90000 }),
  getAggregateRefunds: (params = {}) => api.get('/accounting/refunds/aggregate', { params, timeout: 120000 }),
  // Refunds split by the channel that acquired the customer. Manifold-billed
  // clinics only — core records no refunds to split.
  getRefundsBySource: (params = {}) => api.get('/accounting/refunds/by-source', { params, timeout: 90000 }),
}

// Queues/Reports API
export const queuesAPI = {
  getStatusDistribution: (params = {}) => api.get('/queues/status-distribution', { params }),
  getOrdersByStatus: (status, params = {}) => api.get(`/queues/orders/${status}`, { params }),
  getStatusSummary: (params = {}) => api.get('/queues/status-summary', { params }),
  getAttention: () => api.get('/queues/attention'),
}

// Order Flows/Timing Analysis API (longer timeout for complex aggregations)
export const orderFlowsAPI = {
  getStepAnalysis: (params = {}) => api.get('/order-flows/step-analysis', { params, timeout: 300000 }), // 5 min: multi-clinic fans out
  getClinics: () => api.get('/order-flows/clinics', { timeout: 30000 }),
  getFilters: (params = {}) => api.get('/order-flows/filters', { params, timeout: 60000 }),
  getDeciles: (params = {}) => api.get('/order-flows/deciles', { params, timeout: 60000 }),
  getStepDefinitions: () => api.get('/order-flows/step-definitions'),
  getDurationTrends: (params = {}) => api.get('/order-flows/duration-trends', { params, timeout: 300000 }),
  getRouting: (params = {}) => api.get('/order-flows/routing', { params, timeout: 60000 }),
}

export const pharmacyCountAPI = {
  getReport: (params = {}) => api.get('/pharmacy-count', { params, timeout: 120000 }),
}

// Prescriptions written per prescriber. Two sources behind one report — core
// clinics from each clinic's `scripts` collection, MDExam (Legacy) from
// embedded orders — so the response carries a `basis` per row. See
// docs/concepts/prescriptions.md.
export const prescriberAPI = {
  getReport: (params = {}) => api.get('/prescribers', { params, timeout: 120000 }),
  // Super admin only: which DoseSpot clinic ids are live (diagnostic, not the report source).
  getDosespotClinics: (params = {}) => api.get('/prescribers/dosespot-clinics', { params, timeout: 120000 }),
  getLegacyScripts: (doctorId, params = {}) =>
    api.get(`/prescribers/legacy/${encodeURIComponent(doctorId)}/scripts`, { params, timeout: 120000 }),
  getCoreScripts: (clinicId, physicianId, params = {}) =>
    api.get(
      `/prescribers/core/${encodeURIComponent(clinicId)}/${encodeURIComponent(physicianId)}/scripts`,
      { params, timeout: 120000 }
    ),
}

export const progressBoostAPI = {
  getSummary: (params = {}) => api.get('/progress-boost/summary', { params, timeout: 120000 }),
  getFunnel: (params = {}) => api.get('/progress-boost/funnel', { params, timeout: 120000 }),
  getFlow: (params = {}) => api.get('/progress-boost/flow', { params, timeout: 120000 }),
  getJourneys: (params = {}) => api.get('/progress-boost/journeys', { params, timeout: 120000 }),
  getJourneyDetail: (sessionId, params = {}) =>
    api.get(`/progress-boost/journeys/${encodeURIComponent(sessionId)}`, { params, timeout: 120000 }),
}

export const pharmacyBenefitAPI = {
  getSummary: (params = {}) => api.get('/pharmacy-benefit/summary', { params, timeout: 120000 }),
  getDrugs: (params = {}) => api.get('/pharmacy-benefit/drugs', { params, timeout: 120000 }),
  getPayers: (params = {}) => api.get('/pharmacy-benefit/payers', { params, timeout: 120000 }),
  getFillPatterns: (params = {}) => api.get('/pharmacy-benefit/fill-patterns', { params, timeout: 120000 }),
  getPatients: (params = {}) => api.get('/pharmacy-benefit/patients', { params, timeout: 120000 }),
  getTrends: (params = {}) => api.get('/pharmacy-benefit/trends', { params, timeout: 120000 }),
}

export const rewardsAPI = {
  listCatalog: (params = {}) => api.get('/rewards/catalog', { params, timeout: 60000 }),
  createReward: (body) => api.post('/rewards/catalog', body),
  listAssignments: (params = {}) => api.get('/rewards/assignments', { params }),
  assign: (body) => api.post('/rewards/assign', body, { timeout: 60000 }),
  getActivity: (segmentIds) => api.post('/rewards/activity', { segmentIds }, { timeout: 60000 }),
  getActivePool: (params = {}) => api.get('/rewards/active-pool', { params, timeout: 60000 }),
  assignActive: (body) => api.post('/rewards/assign-active', body, { timeout: 120000 }),
}

export const marketingAPI = {
  getChannels: () => api.get('/marketing/channels'),
  getSpend: (params = {}) => api.get('/marketing/spend', { params }),
  getTrends: (params = {}) => api.get('/marketing/trends', { params }),
  getCampaigns: (params = {}) => api.get('/marketing/campaigns', { params, timeout: 60000 }),
  // Attribution credit per campaign/adset/ad; reads every journey, hence the timeout.
  getCampaignAttribution: (params = {}) => api.get('/marketing/campaigns/attribution', { params, timeout: 120000 }),
  // The same credit by channel → campaign → content, UTMs filling in where ad_spend cannot (Channels report rows).
  getChannelCampaignAttribution: (params = {}) => api.get('/marketing/campaigns/channel-attribution', { params, timeout: 120000 }),
  // Aggregate endpoints for "All Clinics" view
  getSpendAggregate: (params = {}) => api.get('/marketing/spend/aggregate', { params, timeout: 60000 }),
  getTrendsAggregate: (params = {}) => api.get('/marketing/trends/aggregate', { params, timeout: 60000 }),
}

// Traffic Analytics API (BigQuery/Segment)
// Slack notifications: workspace install, per-clinic channel routing
export const slackAPI = {
  getConfig: () => api.get('/slack/config'),
  saveConfig: (payload) => api.put('/slack/config', payload),
  getChannels: () => api.get('/slack/channels', { timeout: 30000 }),
  startOAuth: () => api.get('/slack/oauth/start'),
  disconnect: () => api.delete('/slack/installation'),
  test: (channelId) => api.post('/slack/test', { channelId }),
  runDailySummary: (date) => api.post('/slack/daily-summary/run', date ? { date } : {}, { timeout: 120000 }),
}

// Daily Report API — one row per day / ISO week / month for the selected clinic
export const dailyReportAPI = {
  getCustomerAttribution: (patientId, params = {}) => api.get(`/daily-report/attribution-models/customer/${encodeURIComponent(patientId)}`, { params, timeout: 120000 }),
  get: (params = {}) => api.get('/daily-report', { params, timeout: 120000 }),
  getAttributionModels: (params = {}) => api.get('/daily-report/attribution-models', { params, timeout: 120000 }),
  getProductAttribution: (params = {}) => api.get('/daily-report/attribution-models/products', { params, timeout: 120000 }),
  getCampaignAttribution: (params = {}) => api.get('/daily-report/attribution-models/campaigns', { params, timeout: 120000 }),
  getLift: (params = {}) => api.get('/daily-report/attribution-models/lift', { params, timeout: 180000 }),
}

// Per-rep sales on the sales_team register
export const salesTeamAPI = {
  get: (params = {}) => api.get('/sales-team', { params, timeout: 120000 }),
}

// Science.co Model vs Actual — the model's inputs as they actually happened
export const scienceModelAPI = {
  getActuals: (params = {}) => api.get('/science-model/actuals', { params, timeout: 120000 }),
  getDailyActuals: (params = {}) => api.get('/science-model/actuals/daily', { params, timeout: 120000 }),
  getDayActuals: (params = {}) => api.get('/science-model/actuals/days', { params, timeout: 120000 }),
  getPrices: () => api.get('/science-model/prices', { timeout: 60000 }),
}

export const velocityAPI = {
  get: (params = {}) => api.get('/velocity', { params, timeout: 120000 }),
  getAlerts: () => api.get('/velocity/alerts', { timeout: 120000 }),
  getPatterns: (params = {}) => api.get('/velocity/patterns', { params, timeout: 120000 }),
  getCompare: (params = {}) => api.get('/velocity/compare', { params, timeout: 120000 }),
}

// Product Marketing API
export const productMarketingAPI = {
  getReport: (params = {}) => api.get('/product-marketing/report', { params, timeout: 120000 }),
  getReportBigQuery: (params = {}) => api.get('/product-marketing/report/bigquery', { params, timeout: 120000 }),
  getFilters: () => api.get('/product-marketing/filters', { timeout: 30000 }),
  // Aggregate endpoint for "All Clinics" view
  getReportAggregate: (params = {}) => api.get('/product-marketing/report/aggregate', { params, timeout: 120000 }),
  // Products page: cells of period × kind × plan (see backend services/productMix)
  getMix: (params = {}) => api.get('/product-marketing/mix', { params, timeout: 120000 }),
  getMixAggregate: (params = {}) => api.get('/product-marketing/mix/aggregate', { params, timeout: 180000 }),
}

export const trafficAPI = {
  getSummary: (params = {}) => api.get('/traffic/summary', { params, timeout: 60000 }),
  getSources: () => api.get('/traffic/sources'),
  getDetails: (params = {}) => api.get('/traffic/details', { params, timeout: 60000 }),
  getBrands: () => api.get('/traffic/brands'),
  getFunnel: (params = {}) => api.get('/traffic/funnel', { params, timeout: 60000 }),
  getDailyFunnel: (params = {}) => api.get('/traffic/daily-funnel', { params, timeout: 60000 }),
  getConversions: (params = {}) => api.get('/traffic/conversions', { params, timeout: 60000 }),
  getSubSourceFunnel: (params = {}) => api.get('/traffic/sub-source-funnel', { params, timeout: 60000 }),
  getToday: () => api.get('/traffic/today', { timeout: 30000 }),
  getCampaigns: (params = {}) => api.get('/traffic/campaigns', { params, timeout: 60000 }),
  getCampaignDrilldown: (params = {}) => api.get('/traffic/campaign-drilldown', { params, timeout: 60000 }),
  getPages: (params = {}) => api.get('/traffic/pages', { params, timeout: 60000 }),
  getAliases: () => api.get('/traffic/aliases'),
  updateAliases: (data) => api.put('/traffic/aliases', data),
  addAlias: (data) => api.post('/traffic/aliases/single', data),
  removeAlias: (raw_name) => api.delete('/traffic/aliases/single', { params: { raw_name } }),
  // Attribution endpoints
  getAttributionFlows: (params = {}) => api.get('/traffic/attribution/flows', { params, timeout: 120000 }),
  getRetargeting: (params = {}) => api.get('/traffic/attribution/retargeting', { params, timeout: 120000 }),
  getAttributionPaths: (params = {}) => api.get('/traffic/attribution/paths', { params, timeout: 60000 }),
  getAttributionPathUsers: (params = {}) => api.get('/traffic/attribution/paths/users', { params, timeout: 60000 }),
  getAttributionFilters: (params = {}) => api.get('/traffic/attribution/filters', { params, timeout: 30000 }),
  getProductByChannel: (params = {}) => api.get('/traffic/attribution/product-by-channel', { params, timeout: 120000 }),
  // Channel performance endpoints
  getChannelPerformance: (params = {}) => api.get('/traffic/channel-performance', { params, timeout: 120000 }),
  getChannelTraffic: (params = {}) => api.get('/traffic/channel-traffic', { params, timeout: 120000 }),
  // The newest pageview BigQuery holds; a same-time comparison of today cuts there.
  getDataFreshness: () => api.get('/traffic/data-freshness', { timeout: 60000 }),
  // Core/Manifold charges vs what BigQuery can see, for the freshness strip.
  getPaymentReconciliation: (params = {}) => api.get('/traffic/payment-reconciliation', { params, timeout: 60000 }),
  // Quality metrics endpoint
  getQuality: (params = {}) => api.get('/traffic/quality', { params, timeout: 60000 }),
  // Depth-weighted quality score + its trend over time
  getQualityScore: (params = {}) => api.get('/traffic/quality-score', { params, timeout: 120000 }),
  getQualityScoreTrend: (params = {}) => api.get('/traffic/quality-score/trend', { params, timeout: 120000 }),
  // POST: the ad names to match are too many/too punctuated for a query string
  getQualitySparklines: (body = {}) => api.post('/traffic/quality-sparklines', body, { timeout: 120000 }),
  getFunnelDurations: (params = {}) => api.get('/traffic/funnel-durations', { params, timeout: 120000 }),
  // Ordered screen-by-screen funnel of one intake funnel (Traffic → Intake Steps)
  getIntakeScreenSteps: (params = {}) => api.get('/traffic/intake-steps', { params: { ...params, mode: 'screens' }, timeout: 120000 }),
  getAdLpAlignment: (params = {}) => api.get('/traffic/ad-lp-alignment', { params, timeout: 120000 }),
  getEngagement: (params = {}) => api.get('/traffic/engagement', { params, timeout: 120000 }),
  getLpClickRate: (params = {}) => api.get('/traffic/lp-click-rate', { params, timeout: 60000 }),
  getLpClickRateDetail: (params = {}) => api.get('/traffic/lp-click-rate/detail', { params, timeout: 60000 }),
  getUserJourneyMismatches: (params = {}) => api.get('/traffic/user-journey/mismatches', { params, timeout: 60000 }),
  getUserJourneyLookup: (params = {}) => api.get('/traffic/user-journey/lookup', { params, timeout: 60000 }),
  getUserJourney: (anonymousId) => api.get(`/traffic/user-journey/${encodeURIComponent(anonymousId)}`, { timeout: 60000 }),
}

// Zendesk Support API
// Patient ↔ care-team messages in core (physician_messaging), Zendesk-style.
export const providerMessagingAPI = {
  getReport: (params = {}) => api.get('/provider-messaging/report', { params, timeout: 60000 }),
}

export const zendeskAPI = {
  getReport: (params = {}) => api.get('/zendesk/report', { params, timeout: 60000 }),
  getWeekly: (params = {}) => api.get('/zendesk/weekly', { params, timeout: 30000 }),
  getReps: (params = {}) => api.get('/zendesk/reps', { params, timeout: 60000 }),
  getReportAggregate: (params = {}) => api.get('/zendesk/report/aggregate', { params, timeout: 60000 }),
  getWeeklyAggregate: (params = {}) => api.get('/zendesk/weekly/aggregate', { params, timeout: 60000 }),
  getTailSample: (params = {}) => api.get('/zendesk/tail-sample', { params, timeout: 60000 }),
  getResponseTimes: (params = {}) => api.get('/zendesk/response-times', { params, timeout: 60000 }),
}

// API Keys API (super admin only)
export const statusAPI = {
  getUptime: () => api.get('/status/uptime'),
}

export const apiKeysAPI = {
  getAll: () => api.get('/api-keys'),
  getByClinic: (clinicId) => api.get(`/api-keys/${clinicId}`),
}

// Admin API (super admin only)
export const adminAPI = {
  getConfigDiff: () => api.get('/admin/config-diff', { timeout: 30000 }),
  getPharmacyDiff: () => api.get('/admin/config-diff/pharmacies', { timeout: 30000 }),
  getPhysicianDiff: () => api.get('/admin/config-diff/physicians', { timeout: 30000 }),

  // MDExam legacy → core migration audit (temporary migration tooling).
  // The id-level diff scans every key on both clusters, so it gets a long timeout.
  // MDExam splits into two core clinics, so every drill-down is scoped to one
  // target ('rrv' | 'wl'). The overview covers both at once.
  getMigrationAudit: () => api.get('/admin/migration-audit', { timeout: 120000 }),
  getMigrationConfigDiff: (kind, target) =>
    api.get(`/admin/migration-audit/config/${kind}`, { params: { target }, timeout: 60000 }),
  getMigrationQueues: (target) =>
    api.get('/admin/migration-audit/queues', { params: { target }, timeout: 120000 }),
  getMigrationIdDiff: (kind, sample = 100, target) =>
    api.get(`/admin/migration-audit/ids/${kind}`, { params: { sample, target }, timeout: 600000 }),
  // How Manifold is splitting live MDExam billing between WL and RRV.
  getMigrationRouting: () => api.get('/admin/migration-audit/routing', { timeout: 180000 }),

  // Campaign goal tagging. The list aggregates every ad_spend row ever written,
  // so it gets a longer timeout than a typical admin read.
  getCampaignGoals: () => api.get('/admin/campaign-goals', { timeout: 60000 }),
  saveCampaignGoal: (payload) => api.put('/admin/campaign-goals', payload),
  deleteCampaignGoal: (payload) => api.delete('/admin/campaign-goals', { data: payload }),
}

// Storage API (super admin only - S3 bucket browsing)
export const storageAPI = {
  browse: (prefix = '') => api.get('/storage/browse', { params: { prefix } }),
}

// Payments API (Manifold integration)
export const paymentsAPI = {
  // Get all dashboard data in one call
  getDashboard: (params = {}) => api.get('/payments/dashboard', { params, timeout: 60000 }),
  // Individual dashboard endpoints
  getBilling: (params = {}) => api.get('/payments/dashboard/billing', { params, timeout: 30000 }),
  getNewBilling: () => api.get('/payments/dashboard/new-billing', { timeout: 30000 }),
  getSubscriptions: () => api.get('/payments/dashboard/subscriptions', { timeout: 30000 }),
  getSignups: (params = {}) => api.get('/payments/dashboard/signups', { params, timeout: 30000 }),
  getPayments: () => api.get('/payments/dashboard/payments', { timeout: 30000 }),
  getRefunds: () => api.get('/payments/dashboard/refunds', { timeout: 30000 }),
  // Drug and billing-frequency mix read from Manifold's own collections. The
  // dashboard counts endpoint returns only a plan NAME, which cannot express a
  // billing cycle reliably — see services/manifoldPlanMix.
  getPlanMix: () => api.get('/payments/dashboard/plan-mix', { timeout: 30000 }),
  // Payments that were attempted and refused. Read from the tenant's own
  // collections — Manifold's dashboard API exposes settled transactions only,
  // so there is no upstream endpoint for a failure.
  getDeclines: (params = {}) => api.get('/payments/dashboard/declines', { params, timeout: 60000 }),
  // Which processor and merchant account the charges ran through. Also from the
  // tenant's collections: Manifold's dashboard API reports settled totals and
  // never says which connector produced them.
  getGateways: (params = {}) => api.get('/payments/dashboard/gateways', { params, timeout: 60000 }),
  getTenderMix: (params = {}) => api.get('/payments/dashboard/tender-mix', { params, timeout: 60000 }),
  // Status check
  getStatus: () => api.get('/payments/status', { timeout: 10000 }),
}

// Billing Reports API
//
// Distinct from paymentsAPI, which proxies Manifold's own dashboard. Both of
// these are computed here from core: new-customers lists first purchases,
// bundles forecasts pharmacy cost from the core fill schedule.
export const billingReportsAPI = {
  // Scans a window of orders and payments, so a wider timeout than the proxy.
  getNewCustomers: (params = {}) =>
    api.get('/billing-reports/new-customers', { params, timeout: 120000 }),
  getOrderExport: (params = {}) =>
    api.get('/billing-reports/order-export', { params, timeout: 180000 }),
  // The CSV comes back as a file, so the caller downloads it rather than
  // reading a payload — hence responseType and the longer window.
  downloadOrderExport: (params = {}) =>
    api.get('/billing-reports/order-export', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
      timeout: 300000,
    }),
  getBundles: (params = {}) =>
    api.get('/billing-reports/bundles', { params, timeout: 120000 }),
  // Reads the P&L, refunds and declines for the range and the prior range.
  getCfo: (params = {}) =>
    api.get('/billing-reports/cfo', { params, timeout: 180000 }),
}

// User Analytics API
export const analyticsAPI = {
  // Track events (single or batch)
  track: (eventData) => api.post('/analytics/track', eventData),
  trackBatch: (events) => api.post('/analytics/track/batch', { events }),

  // Get analytics data (admin only)
  getSummary: (params = {}) => api.get('/analytics/summary', { params, timeout: 30000 }),
  getUsers: (params = {}) => api.get('/analytics/users', { params, timeout: 30000 }),
  getUserDetail: (userId, params = {}) => api.get(`/analytics/users/${userId}`, { params, timeout: 30000 }),
  getUserSessions: (userId, params = {}) => api.get(`/analytics/users/${userId}/sessions`, { params, timeout: 30000 }),
}

// TV Display API
export const tvDisplayAPI = {
  getDashboard: () => api.get('/tv-display/dashboard', { timeout: 60000 }),
  getSettings: () => api.get('/tv-display/settings', { timeout: 10000 }),
}

export const churnAPI = {
  getReport: (params = {}) => api.get('/churn/report', { params, timeout: 120000 }),
  // Retention split by acquisition channel. Manifold tenants only; Chargebee
  // clinics' event history carries no customer identity to bridge to a channel.
  getBySource: (params = {}) => api.get('/churn/by-source', { params, timeout: 120000 }),
}

// Sex, age and location of a Manifold tenant's active patients, against ACS
// adults. Manifold tenants only.
export const demographicsAPI = {
  getReport: (params = {}) => api.get('/demographics/report', { params, timeout: 120000 }),
}

export const appointmentsAPI = {
  getReport: (params = {}) => api.get('/appointments/report', { params, timeout: 120000 }),
}

export const lanesAPI = {
  getFunnel: (params = {}) => api.get('/lanes/funnel', { params, timeout: 120000 }),
  getSpend: (params = {}) => api.get('/lanes/spend', { params, timeout: 60000 }),
  getMapping: () => api.get('/lanes/mapping', { timeout: 10000 }),
}

export const healthDataAPI = {
  getReport: (params = {}) => api.get('/health-data/report', { params, timeout: 120000 }),
  getGaps: (params = {}) => api.get('/health-data/gaps', { params, timeout: 120000 }),
}

export const disqualifiesAPI = {
  getReport: (params = {}) => api.get('/disqualifies/report', { params, timeout: 60000 }),
  getReportAggregate: (params = {}) => api.get('/disqualifies/report/aggregate', { params, timeout: 60000 }),
}

export const manifoldAPI = {
  getTransactionHealth: (params = {}) => api.get('/manifold/transaction-health', { params, timeout: 120000 }),
  getPaymentSplit: (params = {}) => api.get('/manifold/payment-split', { params, timeout: 120000 }),
  getSubscriptions: (params = {}) => api.get('/manifold/subscriptions', { params, timeout: 120000 }),
  getApiLogs: (params = {}) => api.get('/manifold/api-logs', { params, timeout: 120000 }),
  getCatalog: (params = {}) => api.get('/manifold/catalog', { params }),
  getCouponUsage: (couponId, params = {}) =>
    api.get(`/manifold/coupons/${encodeURIComponent(couponId)}/usage`, { params, timeout: 60000 }),
  getHsSubscriptions: (params = {}) => api.get('/manifold/hs-subscriptions', { params, timeout: 60000 }),
  getRenewalCalendar: (params = {}) => api.get('/manifold/hs-subscriptions/calendar', { params, timeout: 60000 }),
  getRenewalDay: (params = {}) => api.get('/manifold/hs-subscriptions/calendar/day', { params, timeout: 60000 }),
  getDunningEfficacy: (params = {}) => api.get('/manifold/hs-subscriptions/dunning', { params, timeout: 60000 }),
  getBnplRenewals: () => api.get('/manifold/bnpl-renewals', { timeout: 120000 }),
  getHsTransactions: (params = {}) => api.get('/manifold/hs-transactions', { params, timeout: 60000 }),
  // Pricing config (writes) — gated to super_admin / clinic_admin on enabled clinics

  // Transactional email templates. Reads and the write go to Manifold; the
  // preview is rendered by our own backend, so it works on unsaved html.
  getEmailTemplates: (params = {}) => api.get('/manifold/email-templates', { params }),
  getEmailTemplate: (key, params = {}) =>
    api.get(`/manifold/email-templates/${encodeURIComponent(key)}`, { params }),
  // `preheader` travels with the html: it is rendered into the body but is not
  // part of it, so a preview without it shows a placeholder preview line.
  previewEmailTemplate: (key, html, preheader = null, params = {}) =>
    api.post(
      `/manifold/email-templates/${encodeURIComponent(key)}/preview`,
      { html, ...(preheader === null ? {} : { preheader }) },
      { params }
    ),
  saveEmailTemplate: (key, html, params = {}) =>
    api.put(`/manifold/email-templates/${encodeURIComponent(key)}`, { html }, { params }),
}

/**
 * URL for a rendered email preview, to be framed.
 *
 * Same origin, so a plain path. The preview is fetched rather than injected as
 * srcdoc because a srcdoc document inherits this page's CSP, which blocks the
 * S3-hosted logos every template leads with; served from its own URL it gets
 * its own, image-friendly policy instead.
 *
 * Unauthenticated by necessity — an iframe navigation does not run the axios
 * interceptor that attaches the bearer token — so the id is the credential.
 */
export function emailPreviewUrl(previewId) {
  return previewId ? `/api/email-preview/${encodeURIComponent(previewId)}` : null
}

export const roseAPI = {
  getDealerTree: (params = {}) => api.get('/rose/dealer-tree', { params, timeout: 120000 }),
  getDealer: (dealerId, params = {}) => api.get(`/rose/dealer/${dealerId}`, { params, timeout: 30000 }),
}

export const crossSellAPI = {
  getReport: (params = {}) => api.get('/cross-sell/report', { params, timeout: 120000 }),
  getExpansion: (params = {}) => api.get('/cross-sell/expansion', { params, timeout: 120000 }),
}

// Generic API helper functions
export const apiHelpers = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response?.data?.error) {
      const apiError = error.response.data.error
      
      // Handle specific error codes with better user messages
      if (apiError.code === 'CLINIC_SELECTION_REQUIRED') {
        return {
          ...apiError,
          message: 'Please select a specific clinic from the header dropdown to view this data.'
        }
      }
      
      return apiError
    }
    
    if (error.response?.status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: 'Server error occurred. Please try again later.',
      }
    }
    
    if (error.code === 'ECONNABORTED') {
      return {
        code: 'TIMEOUT_ERROR',
        message: 'Request timed out. Please check your connection.',
      }
    }
    
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred.',
    }
  },

  // Format API response data
  formatResponse: (response) => {
    return {
      success: true,
      data: response.data.data,
      meta: response.data.meta,
      pagination: response.data.pagination,
    }
  },

  // Build query string from params
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v))
        } else {
          searchParams.set(key, value)
        }
      }
    })
    
    return searchParams.toString()
  },
}

// Export the configured axios instance as default
export default api
